import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, ScrollReveal, StatsBar, CTASection, TestimonialCard } from '../../components/UI';

const Analytics = () => {
  const metrics = [
    { icon: '📞', title: 'Call Tracking', desc: 'Know exactly which marketing channels drive phone calls. We implement call tracking with recording so you can measure and improve lead quality.' },
    { icon: '📝', title: 'Form Submissions', desc: 'Track every form fill across your website. Know which pages convert and which need optimization.' },
    { icon: '💰', title: 'Revenue Attribution', desc: 'Connect marketing spend to actual revenue. See the true ROI of every campaign and channel.' },
    { icon: '🎯', title: 'Conversion Tracking', desc: 'Track all meaningful actions on your site—not just clicks, but actual business outcomes.' },
    { icon: '📊', title: 'Custom Dashboards', desc: 'Real-time dashboards showing the metrics that matter to your business. No vanity metrics, just actionable data.' },
    { icon: '🔍', title: 'Competitor Analysis', desc: 'Benchmark your performance against competitors. Identify gaps and opportunities in your market.' },
  ];

  const reportSections = [
    { title: 'Executive Summary', items: ['Key wins this month', 'Areas of focus', 'ROI overview', 'Next month priorities'] },
    { title: 'Traffic Analysis', items: ['Organic traffic trends', 'Traffic by source', 'Top landing pages', 'Geographic breakdown'] },
    { title: 'Lead Generation', items: ['Total leads generated', 'Lead source breakdown', 'Cost per lead', 'Lead quality analysis'] },
    { title: 'Rankings & Visibility', items: ['Keyword ranking changes', 'Local pack performance', 'Impression growth', 'Click-through rates'] },
    { title: 'Technical Health', items: ['Site speed metrics', 'Core Web Vitals', 'Crawl errors', 'Index coverage'] },
    { title: 'Recommendations', items: ['Quick wins', 'Strategic initiatives', 'Testing opportunities', '90-day roadmap'] },
  ];

  return (
    <div>
      <PageHeader 
        title="Analytics & Reporting"
        subtitle="Data without insight is just numbers. We transform your marketing data into actionable intelligence that drives growth."
        breadcrumb="Analytics & Reporting"
      />

      <StatsBar />

      {/* Why Analytics */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
            <ScrollReveal>
              <div>
                <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Why Analytics Matter</span>
                <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 20 }}>
                  You Can't Improve What You Can't Measure
                </h2>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>
                  Most businesses are flying blind with their marketing. They spend money on ads and SEO but have no idea what's actually working. Which keywords drive real customers? Which pages convert? What's the true cost per lead?
                </p>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>
                  We believe in complete transparency. You'll always know exactly where your money is going and what results it's generating. Our reports aren't filled with vanity metrics—we focus on numbers that matter.
                </p>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>
                  More importantly, we use this data to continuously optimize your campaigns. Every decision we make is backed by real performance data, not guesswork.
                </p>
                <div style={{ background: '#f8f9fa', borderRadius: 16, padding: 24 }}>
                  <h4 style={{ color: '#1a1a2e', fontSize: 16, fontWeight: 700, marginBottom: 12 }}>What We Track (That Most Agencies Don't)</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {['Actual phone calls generated', 'Lead-to-customer conversion', 'Revenue attributed to SEO', 'True cost per acquisition'].map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#555', fontSize: 14 }}>
                        <span style={{ color: '#ff6b35' }}>✓</span> {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div style={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)', borderRadius: 24, padding: 40 }}>
                <h3 style={{ color: 'white', fontSize: 22, fontWeight: 700, marginBottom: 24 }}>The Crescit Difference</h3>
                <div style={{ marginBottom: 32 }}>
                  <h4 style={{ color: '#ff6b35', fontSize: 16, fontWeight: 600, marginBottom: 16 }}>❌ What Other Agencies Report</h4>
                  {['Impressions (who cares?)', 'Click-through rate (means nothing alone)', 'Keyword rankings (vanity metric)', 'Social followers (not leads)'].map((item, i) => (
                    <div key={i} style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginBottom: 8, paddingLeft: 20 }}>
                      • {item}
                    </div>
                  ))}
                </div>
                <div>
                  <h4 style={{ color: '#22c55e', fontSize: 16, fontWeight: 600, marginBottom: 16 }}>✓ What We Report</h4>
                  {['Leads generated (actual form fills & calls)', 'Cost per lead (real numbers)', 'Lead source (know what\'s working)', 'Revenue impact (ROI you can see)'].map((item, i) => (
                    <div key={i} style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, marginBottom: 8, paddingLeft: 20 }}>
                      • {item}
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* What We Track */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>What We Track</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                Comprehensive Tracking & Attribution
              </h2>
              <p style={{ color: '#666', maxWidth: 600, margin: '0 auto', fontSize: 17, lineHeight: 1.7 }}>
                We set up tracking for everything that matters to your business.
              </p>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 }}>
            {metrics.map((metric, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div style={{ background: 'white', borderRadius: 20, padding: 32, height: '100%', border: '1px solid #eee' }}>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>{metric.icon}</div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>{metric.title}</h3>
                  <p style={{ color: '#666', fontSize: 15, lineHeight: 1.7, margin: 0 }}>{metric.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Monthly Reports */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Monthly Reports</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                Reports That Actually Make Sense
              </h2>
              <p style={{ color: '#666', maxWidth: 700, margin: '0 auto', fontSize: 17, lineHeight: 1.7 }}>
                Our monthly reports tell you exactly what happened, why it matters, and what we're doing next. No jargon, no fluff—just clear insights and actionable recommendations.
              </p>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
            {reportSections.map((section, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div style={{ background: '#f8f9fa', borderRadius: 20, padding: 28, height: '100%' }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #ff6b35' }}>
                    {section.title}
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {section.items.map((item, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, color: '#555', fontSize: 14 }}>
                        <span style={{ color: '#22c55e' }}>✓</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Real-Time Dashboard */}
      <section style={{ padding: '80px 24px', background: 'linear-gradient(135deg, #1a1a2e, #16213e)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
          <ScrollReveal>
            <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Real-Time Dashboard</span>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: 'white', marginTop: 10, marginBottom: 16 }}>
              24/7 Access to Your Data
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', maxWidth: 600, margin: '0 auto 40px', fontSize: 17, lineHeight: 1.7 }}>
              Don't wait for monthly reports. Our custom dashboards give you real-time visibility into your marketing performance whenever you want it.
            </p>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
            {[
              { icon: '📈', title: 'Live Traffic', desc: 'Real-time visitor data' },
              { icon: '📞', title: 'Call Activity', desc: 'Incoming calls tracked' },
              { icon: '📊', title: 'Lead Flow', desc: 'New leads as they come in' },
              { icon: '🎯', title: 'Campaign Performance', desc: 'Ad metrics updated hourly' },
            ].map((item, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: 28, border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>{item.icon}</div>
                  <h4 style={{ color: 'white', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>{item.title}</h4>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, margin: 0 }}>{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Strategy Calls */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
            <ScrollReveal>
              <div>
                <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Strategy Calls</span>
                <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 20 }}>
                  Monthly Strategy Sessions
                </h2>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>
                  Reports are great, but sometimes you need to talk through the data. Every month, we schedule a strategy call to review performance, discuss insights, and align on priorities for the coming month.
                </p>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>
                  These aren't just status updates—they're strategic working sessions where we collaborate on optimizing your marketing for maximum ROI.
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {['Review last month\'s performance', 'Discuss key insights and learnings', 'Identify optimization opportunities', 'Align on next month\'s priorities', 'Answer your questions'].map((item, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, color: '#555', fontSize: 15 }}>
                      <span style={{ color: '#ff6b35' }}>✓</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div style={{ background: 'white', borderRadius: 24, padding: 40, boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
                <h3 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 24 }}>What's Included</h3>
                {[
                  { title: 'Real-Time Dashboard Access', desc: 'Login anytime to see your metrics' },
                  { title: 'Monthly Performance Report', desc: 'Detailed PDF report delivered monthly' },
                  { title: 'Monthly Strategy Call', desc: '30-60 minute video call with your team' },
                  { title: 'Email Support', desc: 'Questions answered within hours' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                    <div style={{ width: 48, height: 48, background: '#f8f9fa', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: '#ff6b35', flexShrink: 0 }}>
                      ✓
                    </div>
                    <div>
                      <h4 style={{ fontSize: 16, fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{item.title}</h4>
                      <p style={{ color: '#666', fontSize: 14, margin: 0 }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <ScrollReveal>
            <TestimonialCard 
              name="Lisa Thompson"
              company="Thompson Property Management"
              text="The transparency is refreshing. For the first time, I actually understand what's happening with our marketing. I can see exactly where our leads come from and what our cost per acquisition is. No more guessing."
            />
          </ScrollReveal>
        </div>
      </section>

      <CTASection 
        title="Ready for Complete Marketing Transparency?"
        subtitle="Let's set up proper tracking so you always know what's working and what's not."
        buttonText="Get Your Free Analytics Audit →"
      />
    </div>
  );
};

export default Analytics;
